import React, { useCallback, useMemo, useState } from 'react'
import { CCard, CCardBody, CSmartTable } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilArrowLeft } from '@coreui/icons'
import moment from 'moment'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import FixtureVotingService from 'src/service/FixtureVotingService'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import RowActionsMenu from 'src/components/common/RowActionsMenu'
import LoadError from './LoadError'
import VoteDetailModal from './VoteDetailModal'
import { useVotingRequest } from './votesUtils'
import './Votes.css'

const SEARCH_FIELDS = [{ key: 'username', label: 'Member' }]

const columns = [
  { key: 'username', label: 'Member', filter: false },
  { key: 'created_at', label: 'Voted On', filter: false, _style: { width: '28%' } },
  { key: 'actions', label: '', filter: false, sorter: false, _style: { width: '12%' } },
]

const DETAIL_COLUMNS = [
  { key: 'player_name', label: 'Player' },
  { key: 'rating_star', label: 'Vote', numeric: true },
]

// Everyone who voted on one fixture, reached from "Votes by Member" on the Fixture Votes tab.
const MemberVotes = () => {
  const { fixtureId } = useParams()
  const { state } = useLocation()
  const navigate = useNavigate()
  const [searchField, setSearchField] = useState(SEARCH_FIELDS[0].key)
  const [searchValue, setSearchValue] = useState('')
  const [itemsPerPage, setItemsPerPage] = useState(20)
  const [member, setMember] = useState(null)

  const request = useCallback(() => FixtureVotingService.userPlayerRating(fixtureId), [fixtureId])
  const { response, loading, error, reload } = useVotingRequest(request)
  const members = useMemo(() => response?.data || [], [response])

  const items = useMemo(() => {
    const term = searchValue.trim().toLowerCase()
    if (!term) return members
    return members.filter((row) =>
      String(row[searchField] ?? '')
        .toLowerCase()
        .includes(term),
    )
  }, [members, searchField, searchValue])

  const load = useMemo(
    () => member && (() => FixtureVotingService.getUserPlayingModal(fixtureId, member.user_id)),
    [member, fixtureId],
  )

  const fixtureLabel = [state?.teamName, state?.dates].filter(Boolean).join(' · ')

  return (
    <>
      <div className="ge-page-header">
        <button
          type="button"
          className="ge-back-link"
          onClick={() => navigate('/votes', { state: { tab: 'fixture' } })}
        >
          <CIcon icon={cilArrowLeft} />
          Back to Votes
        </button>
        <h1 className="ge-page-title">
          Votes by Member {state?.teamName && <>&mdash; {state.teamName}</>}
        </h1>
        <p className="ge-page-subtitle">
          Every member who voted on this fixture. Open a row to see which players they voted for.
        </p>
      </div>

      <CCard className="vt-panel">
        <CCardBody>
          {error ? (
            <LoadError message="We couldn't load the members who voted." onRetry={reload} />
          ) : (
            <>
              <TableSearchToolbar
                fields={SEARCH_FIELDS}
                field={searchField}
                onFieldChange={(field) => {
                  setSearchField(field)
                  setSearchValue('')
                }}
                value={searchValue}
                onSearch={setSearchValue}
                placeholder="Search members..."
              />

              <div className="ge-datatable ge-datatable--no-select">
                <CSmartTable
                  columns={columns}
                  items={items}
                  itemsPerPage={itemsPerPage}
                  itemsPerPageSelect
                  loading={loading}
                  noItemsLabel={
                    searchValue.trim()
                      ? 'No members match your search'
                      : 'No members have voted on this fixture yet'
                  }
                  pagination
                  columnSorter
                  scopedColumns={{
                    username: (item) => <td className="vt-player">{item.username || '—'}</td>,
                    created_at: (item) => (
                      <td className="vt-muted">{moment(item.created_at).format('MMM D, YYYY')}</td>
                    ),
                    actions: (item) => (
                      <td className="text-center">
                        <RowActionsMenu
                          actions={[{ label: 'View Votes', onClick: () => setMember(item) }]}
                        />
                      </td>
                    ),
                  }}
                  tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
                  onItemsPerPageChange={setItemsPerPage}
                />
              </div>
            </>
          )}
        </CCardBody>
      </CCard>

      <VoteDetailModal
        load={load}
        title={member ? `${member.username}'s Votes` : ''}
        subtitle={fixtureLabel}
        columns={DETAIL_COLUMNS}
        emptyLabel="This member has no votes recorded for this fixture."
        onClose={() => setMember(null)}
      />
    </>
  )
}

export default MemberVotes
